import { Accordion, AccordionDetails, AccordionSummary, Card } from '@mui/material';
import styled from 'styled-components';
import { colors, fontFamilyPrimary, fontSize } from '../../../theme';
import { ReactComponent as TemporalFilterImage } from '../../../assets/filters/temporalFilter.svg';

export const TemporalScopeStyled = styled(Card)`
&& {
    background-color: ${colors.grey000};
    border-radius: 8px;
    border: none;
    display: flex;
    width: 100%;
    break-after: avoid;
    break-inside: avoid;
    box-shadow: none;
}`;

export const TemporalScopeElementStyled = styled(Card)`
    && {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        border: none;
        align-items: flex-start;
        position: relative;
        gap: 16px;
        box-shadow: none;
        width: 100%;
        /* The shared form control is sized for the filter bar, the panel wants
           the fixed column width of the design. */
        & .MuiFormControl-root {
            width: 256px;
            min-width: 256px;
            margin-right: 0;
        }
    }
`;

/* Mirrors StyledSelectFormControl so the label sits on the same line as the
   variable labels, and the switch on the same line as their dropdowns. */
export const ComparePeriodStyled = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 5em;
    white-space: nowrap;
    margin: 8px;
`;

export const ComparePeriodToggleStyled = styled.div`
    display: flex;
    align-items: center;
    height: 44px;
    & .MuiFormControlLabel-root {
        margin: 0;
    }
`;

export const DateRangeComparisonStyled = styled.div`
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 16px;
    width: 100%;
`;

export const AccordionStyled = styled(Accordion)(({ height }: {height: string}) => `
    && {
        font-family: ${fontFamilyPrimary};
        background-color: #ffffff;
        font-size: ${fontSize.sm};
        color: ${colors.grey100};
        width: max-content;
        align-items: center;
        /* With nothing selected the pill holds the icon alone, so it shrinks to
           it instead of reserving room for a summary that is not there. */
        min-width: max-content;
        vetical-align: middle;
        border: none;
        box-shadow: none;
        /* MUI positions the Accordion root, which would trap the dropdown panel
           at the width of this pill. Letting it out makes SelectFiltersBar the
           containing block, so the panel can span the whole bar. */
        position: static;
        margin-bottom: 15px;
        margin-right: 0px;
        border-radius: 8px;
        height: ${height};
        ::before {
            display: none;
        };
        &&.Mui-expanded {
            background-color: transparent;
            margin: 0px 0px 15px 0px;
            height: max-content;
        };
        box-shadow: 0px 10px 60px rgba(33,22,69,0.04);
    }
    }
`);

export const AccordionDetailsStyled = styled(AccordionDetails)(({ $positionXY, $fullWidth }: {$positionXY: string, $fullWidth?: boolean}) => `
    && {
        background-color: #FFFFFF;
        box-sizing: border-box;
        padding: 24px;
        width: max-content;
        &&.Mui-selected {
            opacity: 1;
            max-height: 54px;
        };
        position: absolute;
        z-index: 100;
        border-radius: 8px;
        box-shadow: rgba(33, 22, 69, 0.16) 0px 10px 60px;
        margin-top: 16px;
        ${$fullWidth
        // Stretched across SelectFiltersBar, the nearest positioned ancestor,
        // so the selects have the room to sit on one line.
        ? `top: auto;
           left: 0;
           right: 0;
           width: max-content;
           `
        : 'width: max-content;'}
        ${$positionXY}
    }
`);

export const AccordionSummaryStyled = styled(AccordionSummary)(({ $hasSelection = false, $hasComparison = false }: {$hasSelection?: boolean, $hasComparison?: boolean}) => `
    && {
        /* Tinted only once it carries a selection, plain white otherwise. A
           temporal comparison gets its own tint, the one used for its values. */
        background-color: ${($hasComparison && colors.defaultTemporalComparation) || ($hasSelection ? colors.defaultRGBA : colors.grey000)};
        &:hover {
            background-color: ${$hasComparison ? colors.defaultTemporalComparation : colors.defaultRGBA};
        }
        max-height: 54px;
        max-width: 600px;
        border-radius: 8px;
        min-height: 54px;
        width: max-content;
        align-items: center;
        /* MUI wraps the children in this element, so centring the summary itself
           only centres the wrapper. The icon and the labels live in here. */
        & .MuiAccordionSummary-content {
            align-items: center;
            margin: 0;
            gap: 4px;
        }
        /* The icon is an svg, so first-of-type reaches the first summary. */
        & .MuiAccordionSummary-content > div:first-of-type {
            margin-left: 5px;
        }
        & .MuiAccordionSummary-content.Mui-expanded {
            margin: 0;
        }
        &&.Mui-expanded {
            max-height: 54px;
            height:54px;
            margin-right: 10px;
            margin-bottom: 15px;
            min-height: 54px;
            margin: 0;
        };
    }
`);

export const TemporaFilterImageStyled = styled(TemporalFilterImage)`
    && {
        width: 25px;
        height: auto;
        min-width:25px;
    }
`;

export const SelectedFilterSummaryStyled = styled.div`
    && {
        width: auto;
        max-width: 600px;
        min-width: 0;
        font-size: 1em;
        font-weight: 500;
        color: ${colors.default};
        padding-top: 0px;
        white-space: pre-line;
        /* Cut with an ellipsis rather than stretching the pill, while keeping
           the line break the label carries between its title and its value. */
        display: -webkit-box;
        -webkit-line-clamp: 1;
        -webkit-box-orient: vertical;
        overflow: hidden;
        overflow-wrap: anywhere;
    }
`;
